import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
// import { useSelector } from "react-redux";
import EmailDialog from "components/DialogBox/SystemAccess";
import { GlobalDialogBox } from "components/GlobalDialogBox";
import { Loader } from "components/Loader";
import { userHeader } from "./AdministrationHeaders";
import { adminUi, paginationFooter } from "./adminListScreenUi";
import {
  APP_BAR_OFFSET,
  designShell,
  statusChip,
} from "services/constants/designTokens";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import { useSelector } from "react-redux";
import {
  themes,
  scrollTop,
  adminRoleName,
  supportLabel,
} from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";
import moment from "moment";
import { useSavedHeaderViews } from "components/CustomComponents/useSavedHeaderViews";
import { SavedViewSelector } from "components/CustomComponents/SavedViewSelector";
import { useFilter } from "pages/FilterContext";

const USER_LIST_DEFAULT_VIEW_ID = "__default_user_list_view__";
const USER_LIST_DEFAULT_HEADER_KEYS = userHeader.map((header) => header.key);

export const ListView = ({
  openRevokeDialog,
  setOpenRevokeDialog,
  openEmail,
  setOpenEmail,
  checkExistingUser,
  inviteSystemAccess,
  revokeSystemAccess,
  isLoading,
  allUsers,
  masterDataUserDetails,
  dropDown,
  updateMenuItem,
  setEditUserDetails,
  editUser,
  UpdateUserDetails,
  anchorEl,
  setAnchorEl,
  filterChanges,
  setFilterChanges,
  buttonLoader,
  setPage,
  page,
  onRowClick,
  selectedUserId,
  statusFilter,
  subModuleId,
  // View-only actions (download/sort/filter/default-view) -- needs viewer
  // access, not editor. This file has no mutation guard of its own to keep
  // editor-gated (Create/Edit User live in Administration/index.js and
  // UserDetailPanel.js), so the prop only ever needs to carry the one value.
  viewAccess,
}) => {
  // const userDetails = useSelector((state) => state.tokenReducer.userDetails);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filteredUsers, setFilteredUsers] = useState(null);
  // The statusScopedUsers array filteredUsers above was actually computed
  // from — see the filteredUsersReady check below.
  const filteredUsersSourceRef = useRef(null);
  const tableContainerRef = useRef(null);
  // CustomFilter's rendered height isn't fixed — its chip row only appears
  // once a filter/sort is applied — so the table header sticking below it
  // needs this measured, not guessed, to know how far down to offset itself.
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  // Callback ref (not a plain ref) — this component swaps between a Loader
  // and the real content based on `isLoading`, so the DOM node backing
  // customFilterNode unmounts/remounts across that transition. A plain ref
  // + a mount-once effect would measure the very first node and then go
  // stale forever once the Loader swap tears it down; this re-fires (and
  // the effect below re-attaches its observer) every time the node changes.
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);

  useEffect(() => {
    if (!customFilterNode) return;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  useEffect(() => {
    if (!footerNode) return;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);
  const { topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const { isHydratingFilters } = useFilter();

  // Scope CustomFilter's source data to the selected status card first so
  // the table, chip counts, and downloads all operate on the same subset.
  const statusScopedUsers = useMemo(() => {
    if (!statusFilter) return allUsers;
    const wantActive = statusFilter === "active";
    return allUsers.filter(
      (user) => (user?.status === "Active") === wantActive,
    );
  }, [allUsers, statusFilter]);

  // filteredUsers is only trustworthy when it was actually computed from
  // the CURRENT statusScopedUsers — CustomFilter recomputes it in its own
  // effect, one render behind whatever just changed statusScopedUsers (a
  // refetch, a status-card click, the top-bar global search). Tracking the
  // exact array it was derived from (rather than just "is it null") means
  // a stale-but-non-null filteredUsers from the previous statusScopedUsers
  // falls back to the fresh raw data instead of flashing "No Matching
  // Results" for a render before CustomFilter catches up.
  const filteredUsersReady = filteredUsersSourceRef.current === statusScopedUsers;
  const visibleUsers =
    filteredUsers !== null && filteredUsersReady
      ? filteredUsers
      : statusScopedUsers;
  // Always show the pagination footer, regardless of row count -- matches
  // every other table in the app, which don't hide it below a threshold.
  const showPagination = true;
  const emptyStateSourceUsers =
    filteredUsers === null || !filteredUsersReady ? [] : statusScopedUsers;

  useEffect(() => {
    scrollTop(tableContainerRef);
    const handleDialog = () => {
      setOpenRevokeDialog(false);
      setOpenEmail(false);
    };
    handleDialog();
  }, [
    filterChanges,
    setFilterChanges,
    setOpenEmail,
    setOpenRevokeDialog,
    page,
  ]);

  useEffect(() => {
    if (filterChanges) {
      setFilterChanges(false);
    }
  }, [filterChanges, setFilterChanges, setPage]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCloseDialogBox = () => {
    setOpenRevokeDialog(false);
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      filteredUsersSourceRef.current = statusScopedUsers;
      setFilteredUsers(filteredData);
      if (page * rowsPerPage >= filteredData.length) {
        setPage(0);
      }
    },
    [page, rowsPerPage, setPage, statusScopedUsers],
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [topbarCompanyName],
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, [setPage]);

  const {
    savedViewsForScope,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "user_saved_views",
    scope: "default",
    onViewChange: handleSavedViewChange,
    defaultViewId: USER_LIST_DEFAULT_VIEW_ID,
  });
  const persistedDefaultViewId = activeViewId || USER_LIST_DEFAULT_VIEW_ID;
  const [currentViewId, setCurrentViewId] = useState(null);

  useEffect(() => {
    setCurrentViewId((prev) => {
      if (prev && prev !== USER_LIST_DEFAULT_VIEW_ID) {
        return prev;
      }
      return persistedDefaultViewId;
    });
  }, [persistedDefaultViewId]);

  const baseDefaultHeaders = useMemo(() => {
    const keys = new Set(USER_LIST_DEFAULT_HEADER_KEYS);
    return userHeader.filter((header) => keys.has(header.key));
  }, []);

  const storedDefaultView = useMemo(
    () =>
      savedViewsForScope.find(
        (view) => view.id === USER_LIST_DEFAULT_VIEW_ID,
      ) || null,
    [savedViewsForScope],
  );

  const customSavedViews = useMemo(
    () =>
      savedViewsForScope.filter(
        (view) => view.id !== USER_LIST_DEFAULT_VIEW_ID,
      ),
    [savedViewsForScope],
  );

  const defaultView = useMemo(
    () =>
      (storedDefaultView?.isCustomDefault ? storedDefaultView : null) || {
        id: USER_LIST_DEFAULT_VIEW_ID,
        name: "Default View",
        headerConfig: baseDefaultHeaders.map((header) => ({
          key: header.key,
          name: header.name,
        })),
        headerKeys: baseDefaultHeaders.map((header) => header.key),
        isDefaultView: true,
      },
    [baseDefaultHeaders, storedDefaultView],
  );

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;

  const activeView = useMemo(() => {
    if (effectiveViewId === USER_LIST_DEFAULT_VIEW_ID) {
      return defaultView;
    }

    return (
      customSavedViews.find((view) => view.id === effectiveViewId) ||
      defaultView
    );
  }, [customSavedViews, defaultView, effectiveViewId]);

  const visibleHeaders = useMemo(() => {
    const headerByKey = new Map(
      userHeader.map((header) => [header.key, header]),
    );

    if (activeView?.headerConfig?.length) {
      const orderedHeaders = activeView.headerConfig
        .map((entry) => {
          const baseHeader = headerByKey.get(entry.key);
          if (!baseHeader) return null;
          return {
            ...baseHeader,
            name: entry.name?.trim() || baseHeader.name,
          };
        })
        .filter(Boolean);

      return orderedHeaders.length ? orderedHeaders : baseDefaultHeaders;
    }

    if (activeView?.headerKeys?.length) {
      const selectedKeys = new Set(activeView.headerKeys);
      const filteredHeaders = userHeader.filter((header) =>
        selectedKeys.has(header.key),
      );
      return filteredHeaders.length ? filteredHeaders : baseDefaultHeaders;
    }

    return baseDefaultHeaders;
  }, [activeView, baseDefaultHeaders]);

  const handleSelectView = useCallback(
    (viewId) => {
      setCurrentViewId(viewId);
      handleSavedViewChange();
    },
    [handleSavedViewChange],
  );

  const handleSelectDefaultView = useCallback(() => {
    setCurrentViewId(USER_LIST_DEFAULT_VIEW_ID);
    handleSavedViewChange();
  }, [handleSavedViewChange]);

  const handleMakeDefaultView = useCallback(
    (viewId) => {
      if (viewId === USER_LIST_DEFAULT_VIEW_ID) {
        selectDefaultView();
        setCurrentViewId(USER_LIST_DEFAULT_VIEW_ID);
        return;
      }

      selectView(viewId);
      setCurrentViewId(viewId);
    },
    [selectDefaultView, selectView],
  );

  const handleCreateView = useCallback(
    (viewName, headerConfig) => {
      createView(viewName, headerConfig, { activate: false });
    },
    [createView],
  );

  const handleDeleteView = useCallback(
    (viewId) => {
      if (currentViewId === viewId) {
        setCurrentViewId(USER_LIST_DEFAULT_VIEW_ID);
      }
      deleteView(viewId);
    },
    [currentViewId, deleteView],
  );

  const handleEditDefaultView = useCallback(
    (viewId, viewName, headerConfig) => {
      upsertView(
        viewId || USER_LIST_DEFAULT_VIEW_ID,
        viewName || "Default View",
        headerConfig,
        "Default view updated successfully.",
        { isCustomDefault: true },
      );
      setCurrentViewId(USER_LIST_DEFAULT_VIEW_ID);
    },
    [upsertView],
  );

  const titleOverride = useMemo(
    () => (
      <SavedViewSelector
        headers={userHeader}
        defaultHeaders={baseDefaultHeaders}
        defaultView={defaultView}
        savedViews={customSavedViews}
        activeView={activeView}
        activeViewId={effectiveViewId}
        persistedDefaultViewId={persistedDefaultViewId}
        screenAccess={viewAccess}
        onSelectDefaultView={handleSelectDefaultView}
        onSelectView={handleSelectView}
        onCreateView={handleCreateView}
        onUpdateView={(viewId, viewName, headerConfig) => {
          if (viewId === USER_LIST_DEFAULT_VIEW_ID) {
            handleEditDefaultView(viewId, viewName, headerConfig);
            return;
          }
          updateView(viewId, viewName, headerConfig);
        }}
        onDeleteView={handleDeleteView}
        onMakeDefaultView={handleMakeDefaultView}
      />
    ),
    [
      activeView,
      baseDefaultHeaders,
      viewAccess,
      customSavedViews,
      defaultView,
      effectiveViewId,
      handleCreateView,
      handleDeleteView,
      handleEditDefaultView,
      handleMakeDefaultView,
      handleSelectDefaultView,
      handleSelectView,
      persistedDefaultViewId,
      updateView,
    ],
  );

  const getDepartmentNames = useCallback((value) => {
    if (Array.isArray(value)) {
      return value
        .map((item) =>
          typeof item === "string"
            ? item.trim()
            : item?.department_name?.trim?.() || item?.name?.trim?.() || "",
        )
        .filter(Boolean);
    }

    if (typeof value === "string") {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [];
  }, []);

  const formatDepartmentCell = useCallback(
    (value) => {
      const departmentNames = getDepartmentNames(value);

      if (departmentNames.length <= 2) {
        return departmentNames.join(", ") || "N/A";
      }

      return `${departmentNames.slice(0, 2).join(", ")} + ${departmentNames.length - 2}`;
    },
    [getDepartmentNames],
  );

  return (
    <React.Fragment>
      {isLoading || isHydratingFilters ? (
        <Loader info="Loading..." />
      ) : (
        <Mui.Box sx={{ px: 2, mt: 1 }}>
          <Mui.Box ref={customFilterRef} sx={adminUi.stickyCustomFilterBar}>
            <CustomFilter
              title="Users"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={statusScopedUsers}
              filteredData={visibleUsers}
              filteredDataReady={filteredUsers !== null && filteredUsersReady}
              onFilteredDataChange={handleFilteredDataChange}
              // Was hardcoded true (any signed-in user, even one with no
              // access at all) -- download/sort/filter/default-view are
              // view-only actions, so gate on real viewer access instead,
              // matching TeamZone Reports' pattern.
              screenAccess={viewAccess}
              hideCount={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={reportMetadata}
              labeledToolbar={true}
              searchPlaceholder="Search..."
            />
          </Mui.Box>
          <Mui.Box sx={{ ...adminUi.sectionCard, mt: 1.5, borderRadius: 0 }}>
            <Mui.TableContainer
              sx={{
                width: "100%",
                overflowX: "visible",
                pb: showPagination && footerHeight ? `${footerHeight}px` : 0,
              }}
              ref={tableContainerRef}
            >
              <ApproveTable stickyHeader>
                <Mui.TableHead sx={designShell.tableStickyHead}>
                  <Mui.TableRow
                    maxWidth="xl"
                    align="left"
                    sx={designShell.tableStickyHeadRow}
                  >
                    {visibleHeaders?.map?.((value, headerIndex) => (
                      <Mui.TableCell
                        key={value.key || "action"}
                        sx={{
                          ...adminUi.tableHeadCell,
                          top: APP_BAR_OFFSET + customFilterHeight,
                          ...(headerIndex === 0
                            ? { borderTopLeftRadius: 0 }
                            : {}),
                          ...(headerIndex === visibleHeaders.length - 1
                            ? { borderTopRightRadius: 0 }
                            : {}),
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {visibleUsers?.length === 0 ? (
                    <TableEmptyState
                      dataLength={emptyStateSourceUsers}
                      colSpan={visibleHeaders.length}
                    />
                  ) : (
                    visibleUsers
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      ?.map((data) => (
                        <Mui.TableRow
                          key={data?.id}
                          hover
                          onClick={() => onRowClick?.(data)}
                          sx={{
                            cursor: onRowClick ? "pointer" : "default",
                            ...designShell.tableRowHover,
                            ...(data?.id === selectedUserId
                              ? adminUi.selectedRow
                              : {}),
                            "&:last-child td": { borderBottom: "none" },
                          }}
                        >
                          <>
                            {visibleHeaders.map((head) =>
                              head.key === "user_name" ? (
                                <Mui.TableCell
                                  key={head.key}
                                  align="left"
                                  sx={adminUi.tableBodyCell}
                                >
                                  <Mui.Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Mui.Avatar
                                      sx={{ width: 28, height: 28 }}
                                      alt={data?.user_name
                                        ?.charAt(0)
                                        .toUpperCase()}
                                      src={data?.image_url}
                                    />
                                    <Mui.Typography
                                      sx={{
                                        fontWeight:
                                          data?.id === selectedUserId
                                            ? 700
                                            : 500,
                                        color:
                                          data?.id === selectedUserId
                                            ? "#0F172A"
                                            : "inherit",
                                        fontSize: 14,
                                      }}
                                    >
                                      {data?.user_name
                                        ? data?.user_name
                                        : "N/A"}
                                    </Mui.Typography>
                                  </Mui.Stack>
                                </Mui.TableCell>
                              ) : head.key === "status" ? (
                                <Mui.TableCell
                                  key={head.key}
                                  align="left"
                                  sx={adminUi.tableBodyCell}
                                >
                                  {statusChip(data?.status || "Inactive")}
                                </Mui.TableCell>
                              ) : head.key === "department" ? (
                                <Mui.TableCell
                                  key={head.key}
                                  sx={adminUi.tableBodyCell}
                                >
                                  <Mui.Tooltip
                                    title={getDepartmentNames(
                                      data?.[head.key],
                                    ).join(", ")}
                                    arrow
                                    placement="top"
                                    slotProps={tooltipStyleProps}
                                  >
                                    <Mui.Box
                                      component="span"
                                      sx={{
                                        display: "inline-block",
                                        maxWidth: "100%",
                                        cursor: "default",
                                      }}
                                    >
                                      {formatDepartmentCell(data?.[head.key])}
                                    </Mui.Box>
                                  </Mui.Tooltip>
                                </Mui.TableCell>
                              ) : (
                                <Mui.TableCell
                                  key={head.key}
                                  sx={adminUi.tableBodyCell}
                                >
                                  {data[head.key] ? data[head.key] : "N/A"}
                                </Mui.TableCell>
                              ),
                            )}
                          </>
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </ApproveTable>
            </Mui.TableContainer>
            {showPagination && (
              <Mui.Box ref={footerRef} sx={adminUi.stickyFooterBar}>
                {paginationFooter({
                  page,
                  rowsPerPage,
                  total: visibleUsers.length,
                  itemLabel: "users",
                  onPageChange: setPage,
                  onRowsPerPageChange: handleChangeRowsPerPage,
                })}
              </Mui.Box>
            )}
          </Mui.Box>
        </Mui.Box>
      )}
      <GlobalDialogBox
        handleCloseDialog={handleCloseDialogBox}
        open={openRevokeDialog}
        title="Revoke System Access"
      >
        <Mui.Grid container>
          <Mui.Grid item xs={12}>
            <Mui.Typography fontSize={22}>
              Are you sure you want to revoke system access for{" "}
              <b style={{ color: themes.redColor }}>{editUser?.user_name}</b>
              &nbsp;user?
            </Mui.Typography>
          </Mui.Grid>
        </Mui.Grid>
        <Mui.Grid container>
          <Mui.Grid item xs={8}></Mui.Grid>
          <Mui.Grid item xs={2}>
            <CustomButton
              actionFuntion={handleCloseDialogBox}
              actionName="No"
              typeName="submit"
            />
          </Mui.Grid>
          <Mui.Grid item xs={2}>
            <CustomButton
              actionFuntion={() =>
                revokeSystemAccess(editUser?.id, editUser?.email)
              }
              actionName="Yes"
              typeName="submit"
              disableAction={
                openRevokeDialog && buttonLoader ? buttonLoader : false
              }
            />
          </Mui.Grid>
        </Mui.Grid>
      </GlobalDialogBox>
      <EmailDialog
        openDialog={openEmail}
        setOpenDialog={setOpenEmail}
        checkExistingUser={checkExistingUser}
        inviteSystemAccess={inviteSystemAccess}
        editUser={editUser}
        buttonLoader={buttonLoader}
      />
      {/* The row-level "more actions" kebab was removed from the list view
          (same actions are already reachable via "More Actions" in the
          user's detail panel), but this Menu still needs to exist somewhere
          so that panel button has something to open — it's driven entirely
          by shared state (anchorEl/dropDown/editUser) rather than any
          specific row, so a single instance here covers every trigger. */}
      <Mui.Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        elevation={0}
        PaperProps={{
          sx: designShell.actionMenuPaper,
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
      >
        {dropDown.map((option) =>
          option.dropMenu !== "Make TeamLead" ? (
            <Mui.Stack key={option.dropMenu}>
              <Mui.MenuItem
                sx={designShell.actionMenuItem}
                onClick={(e) => {
                  e.stopPropagation();
                  UpdateUserDetails(option.dropMenu);
                  handleMenuClose();
                }}
              >
                {option.dropMenu === "Inactive"
                  ? "Inactivate"
                  : option.dropMenu === "Active"
                    ? "Activate"
                    : option.dropMenu}
              </Mui.MenuItem>
            </Mui.Stack>
          ) : editUser.role === "User" ||
            [adminRoleName, supportLabel].includes(editUser.role) ? (
            <Mui.Stack key={option.dropMenu}>
              <Mui.MenuItem
                sx={designShell.actionMenuItem}
                onClick={(e) => {
                  e.stopPropagation();
                  UpdateUserDetails(option.dropMenu);
                }}
              >
                {option.dropMenu}
              </Mui.MenuItem>
            </Mui.Stack>
          ) : null,
        )}
      </Mui.Menu>
    </React.Fragment>
  );
};
